import React, { useEffect, useState } from 'react';
import { previewClassifiedQuery } from '/imports/api/fleets';

/**
 * Live match count + sample for a candidate cards query.
 *
 * Shared by the CardList editor and the pickers: a query can be valid JSON,
 * save cleanly, and match nothing -- at which point every bot using it
 * advertises/trades nothing and nobody notices until leads dry up. Showing the
 * count at edit time turns that silent failure into an obvious one.
 */
export const QueryPreview = ({ query, emptyHint }: { query: unknown; emptyHint?: string }) => {
  const [state, setState] = useState<{ ok: boolean; error?: string; count: number; sample: { name: string; set: string }[] } | null>(null);

  useEffect(() => {
    if (query === undefined || query === null) { setState(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await previewClassifiedQuery.call({ query });
        if (!cancelled) setState(res);
      }
      catch (e: any) { if (!cancelled) setState({ ok: false, error: `${e}`, count: 0, sample: [] }); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [JSON.stringify(query ?? null)]);

  if (!state) {
    return <div className="text-caption text-fg-3">{emptyHint ?? 'No query selected.'}</div>;
  }
  if (!state.ok) return <div className="text-caption text-red-2">Query error: {state.error}</div>;
  return (
    <div className="rounded-card border border-soft bg-panel-alt p-3">
      <div className="eyebrow text-fg-3">
        matches {state.count >= 1000 ? '1000+' : state.count} card{state.count === 1 ? '' : 's'}
      </div>
      <div className="mt-0.5 text-caption text-fg-3">
        Before the ad filters: the posted classified also excludes foils and anything priced at or
        under the ad floor, and buy/sell ads keep only cards with a matching target.
      </div>
      {state.count === 0 && (
        <div className="mt-1 text-caption text-warn">
          No cards match. Bots using this selection would advertise or trade nothing.
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {state.sample.map((c, i) => (
          <span key={i} className="rounded-full border border-mid px-2 py-0.5 text-[11px] text-fg-2">
            {c.name} <span className="font-mono text-fg-3">{c.set}</span>
          </span>
        ))}
      </div>
    </div>
  );
};
